import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Check, Sparkle, Crown, Buildings, Lightning, ArrowRight, CircleNotch,
  CaretLeft, Rocket, Star, Trophy,
} from "@phosphor-icons/react";
import { API, getAuthHeaders } from "@/lib/api";

const TIER_ORDER = ["free", "starter", "creator", "pro", "studio", "enterprise"];

const TIER_ICONS = {
  free: Sparkle,
  starter: Rocket,
  creator: Star,
  pro: Crown,
  studio: Trophy,
  enterprise: Buildings,
};

const TIER_ACCENTS = {
  free: "bg-white border-zinc-200",
  starter: "bg-white border-zinc-200",
  creator: "bg-white border-[#E91E8C] ring-4 ring-[#E91E8C]/10 shadow-xl md:-mt-4",
  pro: "bg-white border-zinc-200",
  studio: "bg-white border-zinc-200",
  enterprise: "bg-gradient-to-br from-zinc-900 to-zinc-800 border-zinc-900 text-white",
};

const TIER_BADGES = {
  creator: { label: "Most Popular", color: "bg-[#E91E8C] text-white" },
  pro: { label: "Best Value", color: "bg-amber-400 text-zinc-900" },
  enterprise: { label: "For Teams", color: "bg-emerald-500 text-white" },
};

const TIER_TAGLINE = {
  free: "Try the platform, no card required",
  starter: "For creators just getting going",
  creator: "For weekly content that actually converts",
  pro: "For creators shipping every day",
  studio: "For agencies and studios",
  enterprise: "For brands and teams",
};

const featureList = (plan) => {
  const feats = [
    `${plan.videos_per_month} AI-Twin videos/mo`,
    `Up to ${plan.max_seconds_per_video}s per video`,
    plan.allowed_resolutions.includes("720p") ? "HD 720p + 480p" : "480p only",
    plan.watermark ? "Watermarked" : "No watermark",
    plan.priority ? "Priority render queue" : "Standard render queue",
    plan.team_seats > 1 ? `${plan.team_seats} team seats` : "1 seat",
  ];
  return feats;
};

export default function PlanComparison({ onBack, inline = false }) {
  const [plans, setPlans] = useState({});
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // lookup_key
  const [cycle, setCycle] = useState("monthly");
  const [overagePrice, setOveragePrice] = useState(3);
  const [yearlyFreeMonths, setYearlyFreeMonths] = useState(2);

  useEffect(() => {
    (async () => {
      try {
        const [plansRes, meRes] = await Promise.all([
          axios.get(`${API}/plans`),
          axios.get(`${API}/plans/me`, { headers: getAuthHeaders(), withCredentials: true }).catch(() => null),
        ]);
        setPlans(plansRes.data.plans || {});
        setOveragePrice(plansRes.data.overage_price_per_video ?? 3);
        setYearlyFreeMonths(plansRes.data.yearly_discount_months ?? 2);
        if (meRes) setMe(meRes.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const currentTier = me?.tier || "free";

  const handleUpgrade = async (tier) => {
    const isLoggedIn = !!localStorage.getItem("session_token");
    if (!isLoggedIn) {
      window.location.href = `/login?next=/plans&tier=${tier}`;
      return;
    }
    const p = plans[tier];
    if (!p?.stripe_lookup_keys) {
      alert("This plan isn't purchasable online yet — contact us for enterprise pricing.");
      return;
    }
    const lookup_key = p.stripe_lookup_keys[cycle] || p.stripe_lookup_keys.monthly;
    setBusy(lookup_key);
    try {
      const r = await axios.post(
        `${API}/plans/checkout`,
        { lookup_key, origin_url: window.location.origin },
        { headers: getAuthHeaders(), withCredentials: true },
      );
      window.location.href = r.data.checkout_url;
    } catch (e) {
      alert(e.response?.data?.detail || "Checkout failed. Please try again.");
      setBusy(null);
    }
  };

  const displayPrice = (p) => {
    if (!p.price) return 0;
    return cycle === "yearly" ? p.price * (12 - yearlyFreeMonths) : p.price;
  };

  const suffix = cycle === "yearly" ? "/yr" : "/mo";

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <CircleNotch size={32} className="animate-spin text-[#E91E8C]" />
      </div>
    );
  }

  return (
    <div className={inline ? "" : "max-w-7xl mx-auto px-4 py-10"} data-testid="plan-comparison-page">
      {!inline && onBack && (
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-6"
          data-testid="plans-back"
        >
          <CaretLeft size={16} /> Back
        </button>
      )}

      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-1.5 bg-[#E91E8C]/10 text-[#E91E8C] px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider mb-3">
          <Lightning size={12} weight="fill" /> Plans &amp; Pricing
        </div>
        <h1 className="text-3xl sm:text-5xl font-bold text-zinc-900 mb-3">
          One platform. Everything included.
        </h1>
        <p className="text-zinc-500 text-base max-w-2xl mx-auto">
          AI Twin, Auto Create, Scripts, Series, Motion, Long-Form — every tool is in every plan.
          You&apos;re picking the volume that matches your content pace. Upgrade or cancel anytime.
        </p>

        <div className="inline-flex mt-6 rounded-full bg-zinc-100 p-1 text-sm" data-testid="plans-cycle-toggle">
          <button
            onClick={() => setCycle("monthly")}
            className={`px-4 py-1.5 rounded-full font-semibold transition ${cycle === "monthly" ? "bg-white shadow text-zinc-900" : "text-zinc-500"}`}
            data-testid="plans-cycle-monthly"
          >Monthly</button>
          <button
            onClick={() => setCycle("yearly")}
            className={`px-4 py-1.5 rounded-full font-semibold transition ${cycle === "yearly" ? "bg-white shadow text-zinc-900" : "text-zinc-500"}`}
            data-testid="plans-cycle-yearly"
          >Yearly <span className="text-[#E91E8C]">· {yearlyFreeMonths} mo free</span></button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {TIER_ORDER.map((tier) => {
          const plan = plans[tier];
          if (!plan) return null;
          const Icon = TIER_ICONS[tier] || Sparkle;
          const badge = TIER_BADGES[tier];
          const isCurrent = currentTier === tier;
          const isDark = tier === "enterprise";
          const isFree = tier === "free";
          const price = displayPrice(plan);

          return (
            <div
              key={tier}
              data-testid={`plan-card-${tier}`}
              className={`relative rounded-2xl border-2 p-5 flex flex-col ${TIER_ACCENTS[tier]}`}
            >
              {badge && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${badge.color}`}>
                  {badge.label}
                </div>
              )}
              {isCurrent && (
                <div className="absolute -top-3 right-4 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-500 text-white">
                  Current
                </div>
              )}

              <div className="flex items-center gap-2 mb-2">
                <Icon size={22} weight={tier === "creator" ? "fill" : "duotone"} className={isDark ? "text-amber-300" : "text-[#E91E8C]"} />
                <h3 className={`text-lg font-bold ${isDark ? "text-white" : "text-zinc-900"}`}>{plan.name}</h3>
              </div>
              <p className={`text-xs mb-4 ${isDark ? "text-zinc-300" : "text-zinc-500"}`}>{TIER_TAGLINE[tier]}</p>

              <div className="mb-4">
                <div className="flex items-baseline gap-1">
                  <span className={`text-3xl font-bold ${isDark ? "text-white" : "text-zinc-900"}`}>${price}</span>
                  {!isFree && (
                    <span className={`text-xs ${isDark ? "text-zinc-400" : "text-zinc-500"}`}>{suffix}</span>
                  )}
                </div>
                {!isFree && cycle === "yearly" && (
                  <p className={`text-[11px] mt-0.5 ${isDark ? "text-amber-300" : "text-[#E91E8C]"} font-semibold`}>
                    ~${Math.round(price / 12)}/mo billed yearly
                  </p>
                )}
              </div>

              <ul className={`flex-1 space-y-1.5 text-xs mb-5 ${isDark ? "text-zinc-200" : "text-zinc-700"}`}>
                {featureList(plan).map((f) => (
                  <li key={f} className="flex items-start gap-1.5">
                    <Check size={13} weight="bold" className={`flex-shrink-0 mt-0.5 ${isDark ? "text-amber-300" : "text-[#E91E8C]"}`} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {isCurrent ? (
                <div className={`w-full text-center py-2 rounded-lg text-xs font-semibold ${isDark ? "bg-zinc-700 text-zinc-300" : "bg-zinc-100 text-zinc-500"}`}>
                  Your current plan
                </div>
              ) : isFree ? (
                <div className={`w-full text-center py-2 rounded-lg text-xs font-semibold bg-zinc-50 text-zinc-500`}>
                  Auto-assigned on signup
                </div>
              ) : (
                <button
                  onClick={() => handleUpgrade(tier)}
                  disabled={busy !== null}
                  data-testid={`plan-upgrade-${tier}`}
                  className={`w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition ${
                    isDark
                      ? "bg-amber-400 text-zinc-900 hover:bg-amber-300"
                      : tier === "creator"
                      ? "bg-[#E91E8C] text-white hover:bg-[#c91876]"
                      : "bg-zinc-900 text-white hover:bg-zinc-700"
                  } disabled:opacity-50`}
                >
                  {busy && plan.stripe_lookup_keys && busy === plan.stripe_lookup_keys[cycle] ? (
                    <><CircleNotch size={12} className="animate-spin" /> Redirecting…</>
                  ) : (
                    <>Choose {plan.name} <ArrowRight size={12} /></>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Overage explainer */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-5 bg-zinc-50 border border-zinc-200 rounded-xl">
          <h3 className="font-semibold text-zinc-900 mb-1.5 flex items-center gap-1.5">
            <Lightning size={16} className="text-[#E91E8C]" />
            Overage-friendly
          </h3>
          <p className="text-sm text-zinc-600 leading-relaxed">
            Blew past your monthly count on a paid plan? Extra videos are auto-billed at
            <strong> ${overagePrice.toFixed(2)}/video</strong> so your workflow never stops.
            Free tier is a hard cap.
          </p>
        </div>
        <div className="p-5 bg-zinc-50 border border-zinc-200 rounded-xl">
          <h3 className="font-semibold text-zinc-900 mb-1.5 flex items-center gap-1.5">
            <Crown size={16} className="text-[#E91E8C]" />
            Downgrade-friendly
          </h3>
          <p className="text-sm text-zinc-600 leading-relaxed">
            Downgrade any time from Account Settings. You keep every feature of your current plan
            until the end of the billing cycle — no mid-month lockouts.
          </p>
        </div>
        <div className="p-5 bg-zinc-50 border border-zinc-200 rounded-xl">
          <h3 className="font-semibold text-zinc-900 mb-1.5 flex items-center gap-1.5">
            <Check size={16} className="text-[#E91E8C]" />
            Every tool, every plan
          </h3>
          <p className="text-sm text-zinc-600 leading-relaxed">
            Scripts, Series, Bulk, Motion, Long-Form, Scene Studio, Voice Cloning — all included.
            You&apos;re only paying for render volume.
          </p>
        </div>
      </div>

      <div className="mt-6 text-center text-xs text-zinc-400">
        All plans billed via Stripe. Cancel or switch anytime from Account Settings → Subscription.
      </div>
    </div>
  );
}
